import React, { useCallback, useEffect, useRef } from 'react'
import { Container } from 'react-bootstrap'
import './ImageViewer.css'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '@store/hooks'
import { getEnv } from '@utils/env'
import {
  isImageViewerMessageEnvelope,
  IMAGE_VIEWER_MESSAGE_SOURCE,
  type OhifToPmsMessage,
  type OpenNotesForImageEvent,
  type PmsToOhifMessage,
} from '@utils/imageViewerMessages'
import { NoteContextType } from '@dataTypes/note.type'

/**
 * Hook that wires up the bidirectional postMessage bridge between the PMS
 * host window and the OHIF viewer iframe.
 *
 * - Listens for OHIF → PMS events (viewer-closed, report-submitted, annotation-saved)
 * - Exposes a `sendCommand` function for PMS → OHIF commands (navigate-to-study)
 */
function useViewerBridge(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  ohifUrl: string,
): { sendCommand: (message: PmsToOhifMessage) => void } {
  const navigate = useNavigate()

  const handleViewerMessage = useCallback(
    (msg: OhifToPmsMessage): void => {
      switch (msg.type) {
        case 'viewer-closed':
          navigate('/image-viewer', { state: { fromImageViewer: true } })
          break

        case 'open-notes-for-image': {
          const payload = (msg as OpenNotesForImageEvent).payload
          navigate('/notes', {
            state: {
              patientId: payload.patientId || undefined,
              contextType: NoteContextType.FILES,
              contextName: payload.imageFilename || 'X-ray Image',
              initialTitle: payload.suggestedTitle || undefined,
              openFirstNote: true,
              previousPage: { path: '/image-viewer' },
            },
          })
          break
        }

        case 'report-submitted':
          break

        case 'annotation-saved':
          break

        default:
          break
      }
    },
    [navigate],
  )

  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const ohifOrigin = new URL(ohifUrl).origin
      if (event.origin !== ohifOrigin) {
        return
      }

      if (!isImageViewerMessageEnvelope(event.data)) {
        return
      }

      handleViewerMessage(event.data.message as OhifToPmsMessage)
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [ohifUrl, handleViewerMessage])

  const sendCommand = useCallback(
    (message: PmsToOhifMessage): void => {
      const targetWindow = iframeRef.current?.contentWindow
      if (!targetWindow) {
        return
      }

      const ohifOrigin = new URL(ohifUrl).origin
      targetWindow.postMessage(
        { source: IMAGE_VIEWER_MESSAGE_SOURCE, message },
        ohifOrigin,
      )
    },
    [ohifUrl, iframeRef],
  )

  return { sendCommand }
}

/**
 * Warns the user when they try to close the tab or navigate away via the
 * browser's own controls (back button, address bar, refresh) while the
 * image viewer is active. The OHIF viewer may hold unsaved measurements
 * or annotations in memory that would be lost.
 */
function useBeforeUnloadGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return

    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
    }

    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [active])
}

function ImageViewer(): React.ReactElement {
  const navigate = useNavigate()
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const location = useLocation()
  const { files } = (location.state as {
    files?: Array<{ study_uid: string; series_uid: string }>
  }) || { files: [] }
  const fileList = files || []

  const token = useAppSelector((state) => state.auth.accessToken)
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )

  const OHIF_URL = getEnv('REACT_APP_OHIF_URL', 'http://localhost:3000')

  const hasActiveViewer = !!selectedPatient && fileList.length > 0
  useViewerBridge(iframeRef, OHIF_URL)
  useBeforeUnloadGuard(hasActiveViewer)

  const onClickBackButton = (): void => {
    navigate('/image-viewer', { state: { fromImageViewer: true } })
  }

  if (!selectedPatient) {
    return (
      <Container fluid className="border image-viewer-container">
        <button
          className="bg-transparent border-0 back-button"
          onClick={onClickBackButton}
        >
          <i className="ti ti-arrow-left taskbar-icons" />
        </button>
        <div className="d-flex justify-content-center align-items-center h-100">
          <p className="text-muted">
            No patient selected. Please select a patient first.
          </p>
        </div>
      </Container>
    )
  }

  const queryParams = fileList
    .map(
      (file) =>
        `StudyInstanceUIDs=${file.study_uid}&SeriesInstanceUIDs=${file.series_uid}`,
    )
    .join('&')

  return (
    <Container fluid className="border image-viewer-container">
      <button
        className="bg-transparent border-0 back-button"
        onClick={onClickBackButton}
      >
        <i className="ti ti-arrow-left taskbar-icons" />
      </button>
      <iframe
        ref={iframeRef}
        src={`${OHIF_URL}/viewer?${queryParams}&token=${token}&patient_id=${selectedPatient.patient_id}`}
        className="image-viewer-iframe"
        allow="cross-origin-isolated"
        sandbox="allow-same-origin allow-scripts allow-forms allow-downloads"
      />
    </Container>
  )
}

export default ImageViewer
